import moment from "moment";
import strings from "global/constants/StringConstants";
import viewpaths from "global/constants/ViewPathConstants";
import { fillCopy } from "helpers/methods";
import { getApiStatus } from "utils/apiStatus";
import type { WebForm } from "screens/WebForms/WebFormsService";
import {
  AiBuilderParams,
  AiChanges,
  AiDesign,
  AiHistoryMessage,
  AiOutcome,
  AiSessionSummary,
  AiTemplateSummary,
  AiVersion,
  AiWarning,
  ChatEntry,
} from "./AiWebFormTypesAndValidation";

const copy = strings.aiWebForm;

/** `/webforms/ai/<sessionId|new>?templateId=…&signerId=…&name=…`; the opening style travels in router state. */
export const aiBuilderPath = (params: Pick<AiBuilderParams, "sessionId" | "templateId" | "signerId" | "name">) =>
  `${viewpaths.webFormsAiViewPath}/${params.sessionId ?? "new"}?templateId=${encodeURIComponent(
    params.templateId,
  )}&signerId=${params.signerId}&name=${encodeURIComponent(params.name)}`;

// ---------------------------------------------------------------- errors

/** How a failed AI call should be read: by its status, and a 409 by how it begins (stale or a limit). */
export type AiFailure =
  | "busy" // 429 with Retry-After: a turn or start is still running, or a short rate limit
  | "stale" // 409 "The design has changed since version n"
  | "conflict" // any other 409: a limit (turns, versions, publications)
  | "switchedOff" // 503 without Retry-After: the feature is off
  | "retryable" // 503 with Retry-After, 504, a timeout or a network error
  | "notFound" // 404: hidden, another account's, or unknown
  | "other";

export const aiFailure = (error: any): AiFailure => {
  const status = getApiStatus(error);
  const message = aiErrorMessage(error);
  if (status === 429) return "busy";
  // The AI's stale `baseVersion` 409 differs from its limit 409s only in wording (see staleConflictPrefix).
  if (status === 409) return message.startsWith(copy.staleConflictPrefix) ? "stale" : "conflict";
  if (status === 503) return Number(error?.retryAfter) > 0 ? "retryable" : "switchedOff";
  if (status === 504 || status === undefined) return "retryable";
  if (status === 404) return "notFound";
  return "other";
};

/** The AI's own message (written for the user) or the generic one. */
export const aiErrorMessage = (error: any): string => {
  const raw = typeof error === "string" ? error : error?.message;
  return typeof raw === "string" && raw.trim() ? raw : "";
};

/** The longest a Retry-After is waited out: the composer is locked meanwhile. */
export const MAX_RETRY_AFTER_SECONDS = 10;

export const retryAfterMs = (error: any, fallbackSeconds = 2) => {
  const seconds = Number(error?.retryAfter);
  return (seconds > 0 ? Math.min(seconds, MAX_RETRY_AFTER_SECONDS) : fallbackSeconds) * 1000;
};

// ---------------------------------------------------------------- sessions and forms

/**
 * The session a start made, once it shows in the template signer's list: one that was not there
 * before the start and has only its opening turn. Ids, not clocks: the browser's clock and the
 * server's need not agree.
 */
export const startedSession = (
  sessions: AiSessionSummary[],
  before: ReadonlySet<string>,
): AiSessionSummary | undefined =>
  sessions.find((session) => !before.has(session.sessionId) && session.turns <= 1);

/** The session that holds an AI design, for a web form saved without its session id. */
export const sessionOfDesign = (sessions: AiSessionSummary[], designId: string) =>
  sessions.find((session) => session.designId === designId)?.sessionId;

/** Whether publishing this design replaces another active web form of the signer. */
export const replacesAnotherForm = (
  forms: Pick<WebForm, "signerId" | "status" | "aiDesignId">[],
  signerId: number,
  designId: string | null,
) =>
  forms.some(
    (form) => form.signerId === signerId && form.status === "Active" && form.aiDesignId !== designId,
  );

// ---------------------------------------------------------------- versions

/** "Created from your PDF" · "After message #3" · "Restored from v1". */
export const versionOrigin = (
  version: Pick<AiVersion, "origin" | "turn" | "restoredFrom"> | AiDesign | null | undefined,
): string => {
  if (!version) return "";
  if (version.origin === "generated") return copy.createdFromPdf;
  if (version.origin === "restore" && version.restoredFrom) {
    return fillCopy(copy.restoredFrom, { version: version.restoredFrom });
  }
  const turn = version.turn;
  return turn ? fillCopy(copy.afterMessage, { turn }) : "";
};

/** "Today, 10:34 AM", or the date for an older version. */
export const versionWhen = (iso: string | undefined | null): string => {
  if (!iso) return "";
  const at = moment(iso);
  return at.isSame(moment(), "day")
    ? `${copy.today}, ${at.format("h:mm A")}`
    : at.format("MMM D, h:mm A");
};

export const clockTime = (iso: string | null | undefined) =>
  iso ? moment(iso).format("h:mm A") : "";

/** The divider over the conversation: "Today", else its date. */
export const dayLabel = (iso: string | null | undefined) =>
  !iso || moment(iso).isSame(moment(), "day") ? copy.today : moment(iso).format("MMM D, YYYY");

// ---------------------------------------------------------------- what changed

/** The "What changed" chips, from the code-computed `changes` (never the model's words). */
export const changeChips = (changes: AiChanges | undefined): string[] => {
  if (!changes) return [];
  const chips: string[] = [];
  changes.theme.forEach((token) => {
    const name = copy.themeNames[token];
    if (name && !chips.includes(name)) chips.push(name);
  });
  if (changes.texts.length === 1) chips.push(copy.oneQuestionReworded);
  if (changes.texts.length > 1) {
    chips.push(fillCopy(copy.questionsReworded, { count: changes.texts.length }));
  }
  changes.form.forEach((key) => {
    const section = /^section:(\d+)$/.exec(key);
    const name = section
      ? fillCopy(copy.sectionTitle, { number: section[1] })
      : copy.formNames[key];
    if (name && !chips.includes(name)) chips.push(name);
  });
  if (changes.page) chips.push(copy.layoutUpdated);
  return chips;
};

/** Notices under a reply: `contrast_below_aa` first, as the contract asks. */
export const orderWarnings = (warnings: AiWarning[] | undefined): AiWarning[] =>
  [...(warnings ?? [])].sort(
    (a, b) =>
      Number(b.code === "contrast_below_aa") - Number(a.code === "contrast_below_aa"),
  );

/** The suggestion offered under a contrast warning. */
export const contrastSuggestion = (warning: AiWarning) =>
  /button/i.test(warning.message) ? copy.darkerButtonText : copy.easierToRead;

export const isAmber = (warning: AiWarning) =>
  warning.code === "contrast_below_aa" || warning.code.startsWith("label_");

/** The label warnings a publish should show, from a start answer or design get. */
export const labelWarnings = (warnings: AiWarning[] | undefined) =>
  (warnings ?? []).filter(
    (w) => w.code === "label_unwritable" || w.code === "label_shared",
  );

// ---------------------------------------------------------------- chat

export const outcomeChangedNothing = (outcome: AiOutcome | null) =>
  outcome === "unchanged" || outcome === "refused" || outcome === "kept";

/**
 * The session's history (newest first, a page at a time) as chat lines, oldest
 * first. Restores are not messages: the versions list adds them as system
 * lines, placed by time.
 */
export const historyEntries = (
  messages: AiHistoryMessage[],
  restores: AiVersion[],
): ChatEntry[] => {
  const entries: ChatEntry[] = [...messages]
    .sort((a, b) => a.turn - b.turn || (a.role === "user" ? -1 : 1))
    .map((m): ChatEntry =>
      m.role === "user"
        ? {
            kind: "user",
            id: `t${m.turn}-u`,
            text: m.text,
            at: m.at,
            byMe: m.byMe,
          }
        : {
            kind: "assistant",
            id: `t${m.turn}-a`,
            text: m.text,
            at: m.at,
            outcome: m.outcome,
            designVersion: m.designVersion,
            turn: m.turn,
          },
    );
  if (!messages.length) return entries;
  const oldest = messages.reduce(
    (min, m) => (m.at < min ? m.at : min),
    messages[0].at,
  );
  restores
    .filter((v) => v.origin === "restore" && v.createdAt >= oldest)
    .forEach((v) => {
      const index = entries.findIndex((e) => (e.at ?? "") > v.createdAt);
      const line: ChatEntry = {
        kind: "system",
        id: `restore-v${v.version}`,
        text: restoreLine(v),
        at: v.createdAt,
      };
      if (index < 0) entries.push(line);
      else entries.splice(index, 0, line);
    });
  return entries;
};

export const restoreLine = (version: Pick<AiVersion, "restoredFrom" | "byMe">) => {
  const values = { version: version.restoredFrom ?? "" };
  if (version.byMe === true) return fillCopy(copy.restoredByMe, values);
  if (version.byMe === false) return fillCopy(copy.restoredByColleague, values);
  return fillCopy(copy.restoredBySomeone, values);
};

/** "Residential rental application.pdf" · "2 pages · 8 fields for Tenant". */
export const documentChip = (template: AiTemplateSummary | null) => {
  if (!template) return undefined;
  const parts: string[] = [];
  if (template.pageCount) {
    parts.push(
      template.pageCount === 1
        ? copy.onePage
        : fillCopy(copy.manyPages, { count: template.pageCount }),
    );
  }
  parts.push(
    template.signerRole
      ? fillCopy(copy.fieldsFor, { count: template.fieldCount, signer: template.signerRole })
      : fillCopy(copy.fieldsOnly, { count: template.fieldCount }),
  );
  return { name: template.fileName, meta: parts.join(" · ") };
};

/** "Template: Residential rental application · Signer: Tenant". */
export const templateLine = (template: AiTemplateSummary | null, fallbackName?: string) => {
  const name = template?.templateName ?? fallbackName ?? "";
  if (!name) return "";
  return template?.signerRole
    ? fillCopy(copy.templateLine, { template: name, signer: template.signerRole })
    : fillCopy(copy.templateOnlyLine, { template: name });
};

// ---------------------------------------------------------------- preview

/**
 * The page with its thank-you step shown, for the "Show thank-you page"
 * toggle. The HTML is never edited for publishing; this copy is preview only.
 */
export const withThankYouShown = (html: string) => {
  const style =
    "<style>.mz-step{display:none!important}.mz-step-thanks{display:block!important}</style>";
  return /<\/head>/i.test(html) ? html.replace(/<\/head>/i, `${style}</head>`) : style + html;
};

/** The words a publish should list: this version's, less what signers already see. */
export const newPageTexts = (current: string[] | undefined, published: string[] | undefined) =>
  (current ?? []).filter((text) => !(published ?? []).includes(text));

export const messagesCount = (turns: number) =>
  turns === 1 ? copy.oneMessage : fillCopy(copy.manyMessages, { count: turns });
